import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Search, FileText, Check, ShieldCheck, Sparkles, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useDoctorProfile } from '@/hooks/doctor/useDoctorProfile';
import { useToast } from '@/providers/ToastProvider';
import { useCreateCertificate } from '@/hooks';
import { memedApi, doctorsApi, type CertificateType } from '@/api';
import { useMemedPrescription, extractMemedPrescriptionId } from '@/hooks/useMemedPrescription';
import { useMemedConfig } from '@/hooks/useMemedConfig';
import { mapPatientToMemed } from '@/hooks/memed/memed-patient.mapper';
import { obterWorkplaceParaAtendimento } from '@/hooks/memed/workplace-router';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/Textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { CID10Autocomplete } from '@/components/medical-records/CID10Autocomplete';

interface PatientInfo {
  id: string;
  name: string;
  cpf: string;
}

const certificateTypeOptions: { value: CertificateType; label: string }[] = [
  { value: 'SICK_NOTE', label: 'Atestado de Doença' },
  { value: 'HEALTH_CERTIFICATE', label: 'Atestado de Saúde' },
  { value: 'TRAVEL_CERTIFICATE', label: 'Atestado de Viagem' },
  { value: 'OTHER', label: 'Outro' },
];

export function DoctorNewCertificatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { toast } = useToast();
  const { data: doctorProfile } = useDoctorProfile(user?.id || '');
  const doctorId = doctorProfile?.id || user?.id || '';

  const patientIdFromUrl = searchParams.get('patientId');
  const appointmentIdFromUrl = searchParams.get('appointmentId');

  const [selectedPatient, setSelectedPatient] = useState<PatientInfo | null>(null);
  const [patientSearch, setPatientSearch] = useState('');
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);

  const [certificateType, setCertificateType] = useState<CertificateType>('SICK_NOTE');
  const [cidCode, setCidCode] = useState('');
  const [cidDescription, setCidDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [daysOff, setDaysOff] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [observations, setObservations] = useState('');
  const [isOpeningMemed, setIsOpeningMemed] = useState(false);

  const queryClient = useQueryClient();
  const createCertificate = useCreateCertificate();
  const { openPrescription, onMemedEvent, loadScript } = useMemedPrescription();
  const { data: memedConfig } = useMemedConfig();

  // Listener para capturar a assinatura oficial digital MeMed (ICP-Brasil)
  useEffect(() => {
    const unsubscribePrinted = onMemedEvent('prescricaoImpressa', async (data: any) => {
      const memedId = extractMemedPrescriptionId(data);
      if (memedId && selectedPatient) {
        try {
          const docToken = await memedApi.getToken().then((r) => r.token).catch(() => undefined);
          const mentions = data?.prescrição?.additionalData?.mentions || data?.additionalData?.mentions || {};
          const finalDays = Number(mentions.dias_afastamento) || daysOff || 1;
          const finalCid = mentions.CID || mentions.cid || cidCode || undefined;
          const finalReason = cidDescription || reason;
          const finalDiagnosis = finalCid ? `${finalCid} - ${finalReason}` : finalReason;

          await memedApi.syncMemedCertificate({
            appointmentId: appointmentIdFromUrl || undefined,
            patientId: selectedPatient.id,
            doctorId,
            memedPrescriptionId: memedId,
            diagnosis: finalDiagnosis,
            totalDays: finalDays,
            diasAfastamento: finalDays,
            cidCodigo: finalCid,
            tipoDocumento: certificateType,
            textoFormatado: observations || reason,
            horarioAtendimento: mentions.horario_atendimento || undefined,
            nomeAcompanhante: mentions.nome_acompanhante || mentions.acompanhante || undefined,
            startDate: mentions.data_afastamento || startDate || new Date().toISOString(),
            endDate: endDate || undefined,
            doctorToken: docToken,
            pdfUrl: data?.pdfUrl,
            assinadoDigitalmente: true,
            mentions,
          });

          queryClient.invalidateQueries({ queryKey: ['doctor', 'certificates'] });
          queryClient.invalidateQueries({ queryKey: ['medical-records'] });
          toast({
            title: 'Atestado emitido com ICP-Brasil!',
            description: `O atestado digital homologado pela MeMed foi salvo com sucesso (${finalDays} dia${finalDays !== 1 ? 's' : ''}).`,
            variant: 'success',
          });
          navigate('/medico/atestados');
        } catch (err) {
          console.error('[DoctorNewCertificatePage] Erro ao sincronizar atestado MeMed:', err);
        }
      }
    });

    return () => {
      unsubscribePrinted();
    };
  }, [onMemedEvent, selectedPatient, appointmentIdFromUrl, doctorId, daysOff, startDate, endDate, cidCode, cidDescription, reason, observations, certificateType, navigate, queryClient, toast]);

  const handleEmitWithMemed = useCallback(async () => {
    if (!selectedPatient) {
      toast({ title: 'Selecione um paciente', variant: 'error' });
      return;
    }

    if (!reason.trim()) {
      toast({ title: 'Informe a justificativa médica', variant: 'error' });
      return;
    }

    if (!startDate) {
      toast({ title: 'Informe a data de início', variant: 'error' });
      return;
    }

    setIsOpeningMemed(true);
    try {
      let token: string | undefined;
      try {
        const tokenRes = await memedApi.getToken();
        token = tokenRes?.token;
      } catch (err) {
        console.warn('Erro ao obter token MeMed:', err);
      }

      await loadScript(token);

      const memedPatient = mapPatientToMemed({
        id: selectedPatient.id,
        name: selectedPatient.name,
        cpf: selectedPatient.cpf,
      } as any);

      const periodText = daysOff ? `${daysOff} dia(s) de repouso a partir de ${startDate}` : `repouso a partir de ${startDate}`;
      const cidText = cidCode ? ` • CID-10: ${cidCode}${cidDescription ? ` - ${cidDescription}` : ''}` : '';
      const obsText = observations.trim() ? ` • Obs: ${observations.trim()}` : '';

      const certificateItem = {
        nome: `Atestado Médico — Afastamento (${daysOff || 1} dias)`,
        posologia: `Atesto para os devidos fins que o(a) paciente ${selectedPatient.name} necessita de ${periodText}. Justificativa clínica: ${reason.trim()}.${cidText}${obsText}`,
        descricao: 'Atestado Médico',
      };

      const workplaceConfig = obterWorkplaceParaAtendimento(
        searchParams.get('serviceType') || 'TELEMEDICINA',
        memedConfig?.workplace,
      );

      const result = await openPrescription(
        memedPatient,
        workplaceConfig,
        [],
        [],
        { appointmentId: appointmentIdFromUrl || undefined },
        { items: [certificateItem], token }
      );

      if (!result.success) {
        toast({ title: result.error || 'Erro ao abrir emissor MeMed', variant: 'error' });
      }
    } catch (err) {
      toast({ title: 'Não foi possível inicializar a MeMed', variant: 'error' });
    } finally {
      setIsOpeningMemed(false);
    }
  }, [selectedPatient, reason, startDate, daysOff, cidCode, cidDescription, observations, loadScript, openPrescription, memedConfig, appointmentIdFromUrl, searchParams, toast]);

  const { data: patientResults, isLoading: isSearchingPatients } = useQuery({
    queryKey: ['doctor', 'patients', 'search', patientSearch],
    queryFn: async () => {
      const response = await doctorsApi.getMyPatients(1, 20, patientSearch);
      return response.patients;
    },
    enabled: patientSearch.length >= 3 && !patientIdFromUrl,
  });

  const { data: patientFromUrl } = useQuery({
    queryKey: ['patient', patientIdFromUrl],
    queryFn: async () => {
      const response = await doctorsApi.getMyPatients(1, 100);
      return response.patients.find((p) => p.id === patientIdFromUrl);
    },
    enabled: !!patientIdFromUrl,
  });

  useEffect(() => {
    if (patientFromUrl && patientIdFromUrl) {
      setSelectedPatient({
        id: patientFromUrl.id,
        name: patientFromUrl.name,
        cpf: patientFromUrl.cpf,
      });
    }
  }, [patientFromUrl, patientIdFromUrl]);

  useEffect(() => {
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffTime = Math.abs(end.getTime() - start.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      setDaysOff(diffDays);
    } else {
      setDaysOff(null);
    }
  }, [startDate, endDate]);

  const handlePatientSelect = (patient: { id: string; name: string; cpf: string }) => {
    setSelectedPatient(patient);
    setPatientSearch('');
    setShowPatientDropdown(false);
  };

  const handleCIDSelect = (code: string, description: string) => {
    setCidCode(code);
    setCidDescription(description);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedPatient) {
      toast({ title: 'Selecione um paciente', variant: 'error' });
      return;
    }

    if (!reason.trim()) {
      toast({ title: 'Informe a justificativa médica', variant: 'error' });
      return;
    }

    if (!startDate) {
      toast({ title: 'Informe a data de início', variant: 'error' });
      return;
    }

    if (!daysOff || daysOff <= 0) {
      toast({ title: 'Informe o período de afastamento', variant: 'error' });
      return;
    }

    try {
      const certificate = await createCertificate.mutateAsync({
        medicalRecordId: undefined,
        patientId: selectedPatient.id,
        doctorId,
        appointmentId: appointmentIdFromUrl || undefined,
        type: certificateType,
        startDate,
        endDate: endDate || undefined,
        daysOff,
        reason: reason.trim(),
        observations: observations.trim() || undefined,
      });

      if (certificate.id) {
        try {
          await memedApi.sendCertificateToMemed(certificate.id);
          toast({ title: 'Atestado enviado para a MeMed com sucesso', variant: 'success' });
        } catch {
          toast({ title: 'Atestado criado, sincronização com MeMed pendente', variant: 'warning' });
        }
      }

      toast({ title: 'Atestado emitido com sucesso!', variant: 'success' });
      navigate('/medico/atestados');
    } catch (error) {
      toast({ title: 'Erro ao emitir atestado', description: 'Tente novamente', variant: 'error' });
    }
  };

  const formatCPF = (cpf: string) => {
    if (!cpf) return '';
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  return (
    <div className="p-6 space-y-6">
      <div className="max-w-3xl mx-auto space-y-6">
        <button
          onClick={() => navigate('/medico/atestados')}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="text-sm font-medium">Voltar para Atestados</span>
        </button>

        <div className="bg-card text-card-foreground rounded-2xl shadow-sm border border-border p-6 space-y-6">
          <div className="flex items-center gap-3 pb-4 border-b border-border">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
              <FileText className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Novo Atestado Médico</h1>
              <p className="text-sm text-muted-foreground">Preencha os dados do atestado</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">
                Paciente
              </label>
              {selectedPatient ? (
                <div className="flex items-center justify-between p-4 bg-primary/10 border border-primary/20 rounded-xl">
                  <div>
                    <p className="font-semibold text-foreground">{selectedPatient.name}</p>
                    <p className="text-sm text-muted-foreground">CPF: {formatCPF(selectedPatient.cpf)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedPatient(null)}
                    className="text-sm text-primary hover:underline font-semibold"
                  >
                    Alterar
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                      type="text"
                      value={patientSearch}
                      onChange={(e) => {
                        setPatientSearch(e.target.value);
                        setShowPatientDropdown(true);
                      }}
                      onFocus={() => setShowPatientDropdown(true)}
                      placeholder="Buscar paciente por nome ou CPF..."
                      className="w-full pl-10 pr-4 py-3 border border-border bg-background text-foreground rounded-xl focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                    />
                  </div>

                  {showPatientDropdown && patientSearch.length >= 3 && (
                    <div className="absolute z-10 w-full mt-1 bg-card border border-border rounded-xl shadow-lg max-h-60 overflow-auto">
                      {isSearchingPatients ? (
                        <div className="px-4 py-3 text-sm text-muted-foreground text-center">Buscando...</div>
                      ) : patientResults && patientResults.length > 0 ? (
                        <ul className="divide-y divide-border">
                          {patientResults.map((patient) => (
                            <li
                              key={patient.id}
                              className="px-4 py-3 hover:bg-muted cursor-pointer"
                              onClick={() =>
                                handlePatientSelect({
                                  id: patient.id,
                                  name: patient.name,
                                  cpf: patient.cpf,
                                })
                              }
                            >
                              <p className="font-medium text-foreground">{patient.name}</p>
                              <p className="text-sm text-muted-foreground">CPF: {formatCPF(patient.cpf)}</p>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="px-4 py-3 text-sm text-muted-foreground text-center">
                          Nenhum paciente encontrado
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">
                Tipo de Atestado
              </label>
              <Select
                value={certificateType}
                onValueChange={(v) => setCertificateType(v as CertificateType)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {certificateTypeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">
                CID-10
              </label>
              <CID10Autocomplete
                value={cidCode}
                onChange={(code, desc) => handleCIDSelect(code, desc)}
                placeholder="Buscar código CID-10..."
              />
              {cidCode && cidDescription && (
                <p className="mt-2 text-sm text-muted-foreground">
                  <span className="font-mono font-bold text-primary">{cidCode}</span>
                  <span className="mx-2">-</span>
                  <span>{cidDescription}</span>
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">
                Período de Afastamento
              </label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Data início</label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Data fim</label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full"
                  />
                </div>
              </div>
              {daysOff !== null && (
                <p className="mt-2 text-sm font-semibold text-primary">
                  Quantidade de dias: {daysOff} {daysOff === 1 ? 'dia' : 'dias'}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">
                Justificativa Médica *
              </label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Descreva o motivo do atestado médico..."
                rows={4}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">
                Observações (opcional)
              </label>
              <Textarea
                value={observations}
                onChange={(e) => setObservations(e.target.value)}
                placeholder="Observações adicionais..."
                rows={2}
              />
            </div>

            <div className="flex items-start gap-3.5 p-4 bg-primary/5 rounded-xl border border-primary/20">
              <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0 mt-0.5">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-foreground">Certificação Digital ICP-Brasil (MeMed)</h4>
                <p className="text-xs text-muted-foreground mt-0.5">
                  O atestado será emitido e sincronizado com a plataforma MeMed para assinatura digital válida em todo o território nacional (Resolução CFM nº 2.299/2021).
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button
                type="button"
                onClick={handleEmitWithMemed}
                disabled={!selectedPatient || !reason.trim() || !startDate || isOpeningMemed}
                className="flex-1 h-12 text-sm sm:text-base font-bold bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-md shadow-emerald-600/20 gap-2 cursor-pointer"
              >
                {isOpeningMemed ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Conectando com MeMed...</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 text-amber-300" />
                    <span>Emitir e Assinar via MeMed (ICP-Brasil)</span>
                  </>
                )}
              </Button>

              <Button
                type="submit"
                variant="outline"
                disabled={!selectedPatient || !reason.trim() || !startDate || createCertificate.isPending}
                className="h-12 px-6 text-sm font-semibold rounded-xl"
                title="Salvar atestado institucional sem assinatura externa ICP-Brasil"
              >
                {createCertificate.isPending ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    Salvando...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-primary" />
                    Salvar Local
                  </span>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {createCertificate.isPending && <LoadingOverlay message="Emitindo atestado..." />}
      {isOpeningMemed && <LoadingOverlay message="Inicializando emissor MeMed oficial..." />}
    </div>
  );
}
